import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  FormHelperText,
  Typography,
  Grid,
  Box,
} from '@mui/material';

interface QuestionDialogProps {
  open: boolean;
  selectedItem: any;
  categories: Array<{ _id: string; name: string; isActive: boolean }>;
  difficultyLevels: string[];
  questionFormik: any;
  onClose: () => void;
}

export const QuestionDialog: React.FC<QuestionDialogProps> = ({
  open,
  selectedItem,
  categories,
  difficultyLevels,
  questionFormik,
  onClose,
}) => {
  const isEditMode = selectedItem && selectedItem._id;
  
  useEffect(() => {
    console.log('selectedItem:', selectedItem);
    console.log('questionFormik.values:', questionFormik.values);
  }, [selectedItem, questionFormik.values]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Edit' : 'Create'} Question
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <form id="question-form" onSubmit={questionFormik.handleSubmit}>
            <TextField
              fullWidth
              label="Question Text"
              variant="outlined"
              name="question"
              value={questionFormik.values.question}
              onChange={questionFormik.handleChange}
              onBlur={questionFormik.handleBlur}
              error={questionFormik.touched.question && Boolean(questionFormik.errors.question)}
              helperText={questionFormik.touched.question && questionFormik.errors.question}
              sx={{ mb: 2 }}
              multiline
              rows={3}
              required
            />
            
            <Typography variant="h6" sx={{ mb: 2 }}>Answer Options</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Option A"
                  name="options.optionA"
                  value={questionFormik.values.options.optionA}
                  onChange={questionFormik.handleChange}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.options?.optionA && Boolean(questionFormik.errors.options?.optionA)}
                  helperText={questionFormik.touched.options?.optionA && questionFormik.errors.options?.optionA}
                  required
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Option B"
                  name="options.optionB"
                  value={questionFormik.values.options.optionB}
                  onChange={questionFormik.handleChange}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.options?.optionB && Boolean(questionFormik.errors.options?.optionB)}
                  helperText={questionFormik.touched.options?.optionB && questionFormik.errors.options?.optionB}
                  required
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Option C"
                  name="options.optionC"
                  value={questionFormik.values.options.optionC}
                  onChange={questionFormik.handleChange}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.options?.optionC && Boolean(questionFormik.errors.options?.optionC)}
                  helperText={questionFormik.touched.options?.optionC && questionFormik.errors.options?.optionC}
                  required
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Option D"
                  name="options.optionD"
                  value={questionFormik.values.options.optionD}
                  onChange={questionFormik.handleChange}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.options?.optionD && Boolean(questionFormik.errors.options?.optionD)}
                  helperText={questionFormik.touched.options?.optionD && questionFormik.errors.options?.optionD}
                  required
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mb: 2 }}>Question Details</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth error={questionFormik.touched.correctAnswer && Boolean(questionFormik.errors.correctAnswer)}>
                  <InputLabel>Correct Answer</InputLabel>
                  <Select
                    name="correctAnswer"
                    size='small'
                    value={questionFormik.values.correctAnswer}
                    label="Correct Answer"
                    onChange={questionFormik.handleChange}
                    onBlur={questionFormik.handleBlur}
                  >
                    <SelectMenuItem value="A">A</SelectMenuItem>
                    <SelectMenuItem value="B">B</SelectMenuItem>
                    <SelectMenuItem value="C">C</SelectMenuItem>
                    <SelectMenuItem value="D">D</SelectMenuItem>
                  </Select>
                  {questionFormik.touched.correctAnswer && questionFormik.errors.correctAnswer && (
                    <FormHelperText>{questionFormik.errors.correctAnswer}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth error={questionFormik.touched.category && Boolean(questionFormik.errors.category)}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={questionFormik.values.category}
                    label="Category"
                    size='small'

                    onChange={questionFormik.handleChange}
                    onBlur={questionFormik.handleBlur}
                  >
                    {categories.map((category) => (
                      <SelectMenuItem key={category._id} value={category._id}>{category.name}</SelectMenuItem>
                    ))}
                  </Select>
                  {questionFormik.touched.category && questionFormik.errors.category && (
                    <FormHelperText>{questionFormik.errors.category}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth error={questionFormik.touched.difficulty && Boolean(questionFormik.errors.difficulty)}>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    name="difficulty"
                    value={questionFormik.values.difficulty}
                    label="Difficulty"
                    size='small'

                    onChange={questionFormik.handleChange}
                    onBlur={questionFormik.handleBlur}
                  >
                    {difficultyLevels.map((level) => (
                      <SelectMenuItem key={level} value={level}>{level}</SelectMenuItem>
                    ))}
                  </Select>
                  {questionFormik.touched.difficulty && questionFormik.errors.difficulty && (
                    <FormHelperText>{questionFormik.errors.difficulty}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Explanation (Optional)"
                  name="explanation"
                  value={questionFormik.values.explanation}
                  onChange={questionFormik.handleChange}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.explanation && Boolean(questionFormik.errors.explanation)}
                  helperText={questionFormik.touched.explanation && questionFormik.errors.explanation}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Tags (comma-separated)"
                  name="tags"
                  value={questionFormik.values.tags?.join(', ') || ''}
                  onChange={(e) => {
                    const tagsArray = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                    questionFormik.setFieldValue('tags', tagsArray);
                  }}
                  onBlur={questionFormik.handleBlur}
                  error={questionFormik.touched.tags && Boolean(questionFormik.errors.tags)}
                  helperText={questionFormik.touched.tags && questionFormik.errors.tags || 'Enter tags separated by commas (e.g., HR Executive, Finance, Payroll)'}
                  placeholder="HR Executive, Finance, Payroll"
                />
              </Grid>
            </Grid>
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          type="submit"
          form="question-form"
          disabled={!questionFormik.isValid || questionFormik.isSubmitting}
        >
          {isEditMode ? 'Update' : 'Create'} Question
        </Button>
      </DialogActions>
    </Dialog>
  );
};